import { type Component, For, createSignal } from "solid-js";

import type { CreateVmRequest, VmRecord } from "@/api/generated";
import { createVm } from "@/api/vms";

const NAME_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;
const CPU_OPTIONS = [1, 4, 8, 16, 32, 64] as const;
const MEMORY_OPTIONS = [1, 4, 16, 32, 64, 128] as const;
const GPU_OPTIONS = [0, 1, 2, 4] as const;
const SUGGESTED_IMAGES = [
  "alpine:3.19",
  "ubuntu:24.04",
  "debian:12",
  "python:3.12-slim",
  "nvidia/cuda:12.6.3-base-ubuntu22.04",
  "pytorch/pytorch:2.5.1-cuda12.4-cudnn9-runtime",
];

interface Props {
  onCreated: (rec: VmRecord) => void;
}

const LaunchWizard: Component<Props> = (props) => {
  const [name, setName] = createSignal("");
  const [cpu, setCpu] = createSignal(4);
  const [memoryGib, setMemoryGib] = createSignal(16);
  const [gpuCount, setGpuCount] = createSignal(1);
  const [image, setImage] = createSignal(SUGGESTED_IMAGES[0] ?? "");
  const [sshKeysText, setSshKeysText] = createSignal("");
  const [submitting, setSubmitting] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  function validate(): string | null {
    const n = name().trim();
    if (!NAME_RE.test(n)) {
      return "name must be DNS-safe (a-z0-9-, ≤63 chars, start with alphanumeric)";
    }
    if (cpu() < 1 || cpu() > 64) return "cpu must be in [1, 64]";
    if (memoryGib() < 1 || memoryGib() > 512)
      return "memory_gib must be in [1, 512]";
    if (gpuCount() < 0 || gpuCount() > 4) return "gpu_count must be in [0, 4]";
    if (!image().trim()) return "image is required";
    const keys = sshKeysText()
      .split("\n")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (keys.length > 16) return "too many ssh public keys (max 16)";
    for (const k of keys) {
      if (
        !(
          k.startsWith("ssh-rsa ") ||
          k.startsWith("ssh-ed25519 ") ||
          k.startsWith("ecdsa-sha2-")
        )
      ) {
        return `unsupported ssh key prefix: "${k.slice(0, 16)}…"`;
      }
    }
    return null;
  }

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    if (submitting()) return;
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    const sshKeys = sshKeysText()
      .split("\n")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const req: CreateVmRequest = {
      name: name().trim(),
      cpu: cpu(),
      memory_gib: memoryGib(),
      gpu_count: gpuCount(),
      image: image().trim(),
      ssh_public_keys: sshKeys,
    };
    setSubmitting(true);
    setError(null);
    try {
      const rec = await createVm(req);
      props.onCreated(rec);
      setName("");
      setSshKeysText("");
    } catch (caught) {
      const apiErr = caught as { message?: string };
      setError(apiErr.message ?? String(caught));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form class="compute-launch-form" onSubmit={onSubmit}>
      <fieldset>
        <legend>
          <b>1.</b> identity
        </legend>
        <div class="compute-field-grid">
          <div class="field-label">name</div>
          <input
            type="text"
            placeholder="demo-vm-01"
            value={name()}
            onInput={(e) => setName(e.currentTarget.value)}
            required
          />
        </div>
      </fieldset>

      <fieldset>
        <legend>
          <b>2.</b> size
        </legend>
        <div class="compute-field-grid">
          <div class="field-label">cpu</div>
          <Segmented options={CPU_OPTIONS} value={cpu()} setValue={setCpu} />
          <div class="field-label">memory (GiB)</div>
          <Segmented
            options={MEMORY_OPTIONS}
            value={memoryGib()}
            setValue={setMemoryGib}
          />
          <div class="field-label">GPU count</div>
          <div class="compute-seg-row">
            <Segmented
              options={GPU_OPTIONS}
              value={gpuCount()}
              setValue={setGpuCount}
            />
            <span>GB10 · quota-gated</span>
          </div>
        </div>
      </fieldset>

      <fieldset>
        <legend>
          <b>3.</b> image + keys
        </legend>
        <div class="compute-field-grid">
          <div class="field-label">
            image
            <small>docker reference; any registry</small>
          </div>
          <div>
            <input
              type="text"
              list="vm-image-suggestions"
              placeholder="alpine:3.19"
              value={image()}
              onInput={(e) => setImage(e.currentTarget.value)}
              required
            />
            <datalist id="vm-image-suggestions">
              <For each={SUGGESTED_IMAGES}>
                {(img) => <option value={img} />}
              </For>
            </datalist>
          </div>
          <div class="field-label">
            ssh public keys
            <small>one per line; terminal attach works without keys</small>
          </div>
          <textarea
            rows={4}
            placeholder="ssh-ed25519 AAAA... user@host"
            value={sshKeysText()}
            onInput={(e) => setSshKeysText(e.currentTarget.value)}
          />
        </div>
      </fieldset>

      <div class="compute-launch-actions">
        <button type="submit" disabled={submitting()}>
          {submitting() ? "launching…" : "launch VM →"}
        </button>
        <button type="button" class="ghost" disabled>
          save as template
        </button>
        <span>
          est. boot depends on image pull · billing gates enforced by API
        </span>
        {error() && <p class="chat-error">{error()}</p>}
      </div>
    </form>
  );
};

const Segmented: Component<{
  options: readonly number[];
  value: number;
  setValue: (value: number) => void;
}> = (props) => (
  <div class="compute-segmented">
    <For each={props.options}>
      {(option) => (
        <button
          type="button"
          class={props.value === option ? "active" : ""}
          onClick={() => props.setValue(option)}
        >
          {option}
        </button>
      )}
    </For>
  </div>
);

export default LaunchWizard;
